/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJob} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {DeletePrompt} from '@ion/dls/src/editors/DeletePrompt';
import Error from './Error';
import Success from './Success';

// defines the DeviceCancelPrompt Prop type definitions ...
export interface DeviceCancelPromptProps {
  show: boolean;
  downloadjob: DownloadJob | null;
  result?: DownloadJob | null;
  loading?: boolean;
  error?: ServiceError | null;
  overrideMessage?: Function | null;
  onDeleteConfirm: (downloadjob: DownloadJob) => void;
  onDeleteCancel: () => void;
  isCancelDevice?: boolean;
  isJobSearchEngine?: boolean;
}

/**
 * @DeviceCancelPrompt
 *
 * The DeviceCancelPrompt provides the UI for Cancelling an Job action.
 *
 * ```typescript
 *
 *    import {DeviceCancelPrompt} from '@ion/dls/src/editors/';
 *
 *    const {
 *      deviceSelected
 *      selected,
 *      deviceResult,
 *      isEditing,
 *      loading,
 *      error
 *    } = useSelector(({downloadjobs: {cancelDevice}}) => cancelDevice);
 *
 *    const onDeleteConfirm = useCallback(function() {
 *      dispatch(cancelDevice(deviceSelected, selected));
 *    }, [deviceSelected, selected]);
 *
 *    const onDeleteCancel = useCallback(function() {
 *      dispatch(cancelDevices(deviceSelected, selected, false));
 *    }, [deviceSelected, selected]);
 *
 *    return (
 *      <DeviceCancelPrompt show={isEditing}
 *        downloadjob={selected}
 *        onDeleteConfirm={onDeleteConfirm}
 *        onDeleteCancel={onDeleteCancel}
 *        result={result}
 *        loading={loading}
 *        error={error} />
 *    );
 *
 * ```
 *
 * @param {boolean}       show When true, the job delete prompt will be displayed.
 * @param {DownloadJob}   job The selected Job that is to be deleted.
 * @param {DownloadJob}   result The Jov returned by the service after it has been deleted.
 * @param {boolean}       loading When true, indicates that the job is currently being
 *                        deleted.
 * @param {ServiceError}  error A reference to the ServiceError If the deletion fails.
 * @param {Function}      onDeleteConfirm callback function which will be invoked with the
 *                        job when the user confirms the deletion.
 * @param {Function}      onDeleteCancel callback function which will be invoked if the user
 *                        cancels the delete operation.
 */
export const DeviceCancelPrompt = ({
  show,
  downloadjob,
  result,
  loading,
  error,
  onDeleteConfirm,
  onDeleteCancel,
  overrideMessage = null,
  isCancelDevice = false,
  isJobSearchEngine = false
}: DeviceCancelPromptProps) => {
  const onDelete = useCallback(function() {
    onDeleteConfirm(downloadjob);
  }, [downloadjob]);

  const onCancel = useCallback(function() {
    onDeleteCancel();
  }, []);

  if (show || result || error) {
    return (
      <>
        {show && (
          <DeletePrompt show={isJobSearchEngine ? show : (show && !error)}
            entity={translate('downloadjobs:cancelDeviceConfigurator')}
            items={downloadjob}
            isLoading={loading}
            isCancel={isCancelDevice}
            titleMessage={true}
            overrideMessage={overrideMessage}
            onConfirm={onDelete}
            onCancel={onCancel} />
        )}
        <Error label={downloadjob?.esn}
          error={error} />
        <Success label={translate('downloadjobs:delete.deviceSuccess')}
          result={result}
          loading={loading} />
      </>
    );
  }
  return null;
};

export default DeviceCancelPrompt;
